import React from 'react'
import { useIntl } from 'react-intl'

import { BackToPiloteButton } from '@/components/atoms/BackToPiloteButton/BackToPiloteButton.tsx'
import {
  InformationButton,
  type InformationButtonLabels,
} from '@/components/atoms/InformationButton/InformationButton.tsx'
import { SuperPowerButton } from '@/components/atoms/SuperPowerButton/SuperPowerButton.tsx'
import { TelevisionButton, type TelevisionButtonProps } from '@/components/atoms/TelevisionButton/TelevisionButton.tsx'
import {
  AssignationStatus,
  type AssignationStatusProps,
} from '@/components/molecules/AssignationStatus/AssignationStatus.tsx'
import {
  MobileRequestStatusPopup,
  type MobileRequestStatusPopupLabels,
} from '@/components/molecules/MobileRequesetStatusPopup'
import {
  MobileRequest,
  type MobileRequestLabels,
  type MobileRequestProps,
} from '@/components/molecules/MobileRequest/MobileRequest.tsx'
import { NotificationSound } from '@/components/molecules/NotificationSound/NotificationSound.tsx'
import { Region, type RegionProps } from '@/components/molecules/Region/Region.tsx'
import { WaitTime, type WaitTimeLabels, type WaitTimeProps } from '@/components/molecules/WaitTime/WaitTime.tsx'
import { SupportHeaderSlot } from '@/components/organisms/SupportButton/SupportHeaderSlot.tsx'
import { cn } from '@/lib/utils.ts'

export interface HeaderLabels {
  information: InformationButtonLabels
  waitTime: WaitTimeLabels
  mobileRequestStatusPopup: MobileRequestStatusPopupLabels
  mobileRequest: MobileRequestLabels
  assignation: Pick<AssignationStatusProps, 'manualSpecifications' | 'solverSpecifications'>
}

export interface HeaderNavigation {
  television?: Pick<TelevisionButtonProps, 'state'>
}

export interface HeaderData {
  waitTime: Pick<WaitTimeProps, 'state' | 'delay' | 'disabled'>
  mobileRequest: Pick<MobileRequestProps, 'state' | 'disabled'>
  region: Pick<RegionProps, 'state' | 'name' | 'messageTitle' | 'messageText' | 'statuses'>
  assignation: Pick<AssignationStatusProps, 'state' | 'disabled'>
  notificationSoundEnabled: boolean
}

export interface HeaderActions {
  onWaitTimeToggled: () => Promise<void>
  onMobileRequestToggled: () => Promise<void>
  onBackToPiloteClicked: () => Promise<void>
  onChangeAssignationState: (state: AssignationStatusProps['state']) => Promise<void>
  onTelevisionToggled: () => void
  onSuperPower?: () => Promise<void>
  onNotificationSoundToggled?: () => void
}

export interface HeaderProps extends React.HtmlHTMLAttributes<HTMLDivElement>, HeaderActions {
  data: HeaderData
  labels: HeaderLabels
  navigation: HeaderNavigation
}

export const Header = React.forwardRef<HTMLDivElement, HeaderProps>(
  (
    {
      className,
      labels,
      data,
      navigation,
      onWaitTimeToggled,
      onMobileRequestToggled,
      onBackToPiloteClicked,
      onChangeAssignationState,
      onTelevisionToggled,
      onSuperPower,
      onNotificationSoundToggled,
      ...props
    },
    ref
  ) => {
    const intl = useIntl()
    const notificationSoundLabel = intl.formatMessage({
      id: data.notificationSoundEnabled ? 'notificationSound.operations.on' : 'notificationSound.operations.off',
    })
    return (
      <div
        className={cn('relative flex h-[49px] w-full items-center justify-between bg-gray-800 pl-3', className)}
        ref={ref}
        {...props}>
        <div className="flex items-center gap-8">
          <p className="font-heading pl-7 text-xl text-orange-300 select-none">
            {intl.formatMessage({ id: 'app.name' })}
          </p>
          <AssignationStatus {...data.assignation} {...labels.assignation} onValueChange={onChangeAssignationState} />
        </div>
        <div className="flex items-center gap-7">
          <WaitTime labels={labels.waitTime} className="h-fit" {...data.waitTime} onClick={onWaitTimeToggled} />

          <MobileRequestStatusPopup labels={labels.mobileRequestStatusPopup} state={data.mobileRequest.state}>
            <MobileRequest
              labels={labels.mobileRequest}
              className="h-fit"
              {...data.mobileRequest}
              onClick={onMobileRequestToggled}
            />
          </MobileRequestStatusPopup>

          <div className="flex items-center">
            <Region {...data.region} />
            <InformationButton labels={labels.information} className="mx-2" />
            <NotificationSound
              className="mr-2"
              state={data.notificationSoundEnabled ? 'enabled' : 'muted'}
              aria-pressed={data.notificationSoundEnabled}
              aria-label={notificationSoundLabel}
              title={notificationSoundLabel}
              onClick={onNotificationSoundToggled}
            />
            <div className="mr-4 empty:hidden">
              <SupportHeaderSlot />
            </div>
          </div>
        </div>

        <div className="absolute top-12 left-0 flex w-[39px] shrink-0 flex-col items-start justify-center bg-gray-800">
          <BackToPiloteButton onClick={onBackToPiloteClicked} />
          <TelevisionButton state={navigation.television?.state} onClick={onTelevisionToggled} />
          {onSuperPower && <SuperPowerButton onClick={onSuperPower} />}
        </div>
      </div>
    )
  }
)
